import React, {
  useEffect, useState, useCallback, useRef,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { advancedHostingActions } from '@/redux/modules/advancedHosting';

import useLocale from '@/hooks/useLocale';

import * as Styles from './AdvancedHostingPageContent.styles';

import { ADVANCED_HOSTING_DEDICATED_LINUX_URL, ADVANCED_HOSTING_VPS_URL } from '@/config/urls/advancedHostingUrls';

import Header from './components/Header';
import ServerList from './components/ServerList';
import Pagination from './components/Pagination';
import EmptyState from './components/EmptyState';
import NoSearchResult from './components/NoSearchResult';

import ServerTypeModal from './components/ServerTypeModal';

import { commonActions } from '@/redux/modules/common';
import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';

import { Skeleton } from 'gatorcomponents';

function AdvancedHostingPageContent() {
  const dispatch = useDispatch();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const debounceTimerRef = useRef(null);
  const isFirstRender = useRef(true);
  const pageResetByFilter = useRef(false);
  const isInitialLoad = useRef(true);

  const {
    list: advancedHostingList = {},
  } = useSelector(state => state.advancedHosting || {}, shallowEqual);

  const {
    servers = [],
    pagination = {
      currentPage: 1, lastPage: 1, perPage: 10, total: 0,
    },
    loading = false,
  } = advancedHostingList || {};

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: vpsOciLocale } = productDetailsLocale || {};

  const loadingContent = loading && servers.length === 0;
  const emptyPage = !loadingContent && servers.length === 0 && !debouncedSearch;
  const noSearchResult = !loadingContent && servers.length === 0 && debouncedSearch;

  const vpsOci = useSelector(state => state.vpsOci, shallowEqual);
  const urlTerminal = vpsOci?.openSshTerminal?.url || null;

  useEffect(() => {
    if (!loading && servers.length > 0) {
      isInitialLoad.current = false;
    }
  }, [loading, servers.length]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchInput]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (currentPage !== 1) {
      pageResetByFilter.current = true;
      setCurrentPage(1);
    }

    dispatch(advancedHostingActions.list.request({
      search: debouncedSearch,
      sort: sortField,
      order: sortOrder,
      page: 1,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, sortField, sortOrder, dispatch]);

  useEffect(() => {
    if (pageResetByFilter.current) {
      pageResetByFilter.current = false;
      return;
    }

    dispatch(advancedHostingActions.list.request({
      search: debouncedSearch,
      sort: sortField,
      order: sortOrder,
      page: currentPage,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (urlTerminal === 'error') {
      dispatch(commonActions.notifications.set({
        label: vpsOciLocale?.infoSsh?.terminal?.modal?.error?.title,
        type: 'error',
      }));
    }
    dispatch(vpsOciActions.openSshTerminal.set(null));
  }, [urlTerminal, dispatch, vpsOciLocale]);

  const handleSearch = useCallback((searchTerm) => {
    setSearchInput(searchTerm);
  }, []);

  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  }, [sortField, sortOrder]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleSelectVPS = useCallback(() => {
    window.open(ADVANCED_HOSTING_VPS_URL, '_blank');
  }, []);

  const handleSelectDedicated = useCallback(() => {
    window.open(ADVANCED_HOSTING_DEDICATED_LINUX_URL, '_blank');
  }, []);

  const SkeletonListLoader = () => (
    <Styles.SkeletonWrapper data-testid="advanced-hosting-skeleton-loader">
      {[...Array(10)].map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Skeleton key={index} width="100%" height="96px" />
      ))}
    </Styles.SkeletonWrapper>
  );

  const renderContent = () => {
    if (loading && !isInitialLoad.current) {
      return (
        <SkeletonListLoader />
      );
    }

    if (emptyPage) {
      return (
        <EmptyState
          onSelectVPS={handleSelectVPS}
          onSelectDedicated={handleSelectDedicated}
        />
      );
    }

    if (noSearchResult) {
      return (
        <NoSearchResult
          onSelectVPS={handleSelectVPS}
          onSelectDedicated={handleSelectDedicated}
        />
      );
    }

    return (
      <>
        <ServerList
          servers={servers}
          loading={loadingContent}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleSort}
        />

        <Pagination
          currentPage={currentPage}
          lastPage={pagination?.lastPage || 1}
          total={pagination?.total || 0}
          perPage={pagination?.perPage || 10}
          onPageChange={handlePageChange}
        />
      </>
    );
  };

  if (loading && isInitialLoad.current) {
    return (
      <Styles.Wrapper>
        <Styles.Container>
          <SkeletonListLoader />
        </Styles.Container>
      </Styles.Wrapper>
    );
  }

  return (
    <Styles.Wrapper>
      <Styles.Container>
        <Header
          searchInput={searchInput}
          handleSearch={handleSearch}
          onNewServer={handleOpenModal}
          emptyPage={emptyPage}
        />

        {renderContent()}

      </Styles.Container>

      {(!emptyPage && isModalOpen) && (
        <ServerTypeModal
          onClose={handleCloseModal}
          onSelectVPS={handleSelectVPS}
          onSelectDedicated={handleSelectDedicated}
        />
      )}
    </Styles.Wrapper>
  );
}

export default AdvancedHostingPageContent;
